// SPDX-FileCopyrightText: 2023 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import { ModelClassMessage } from '@tls/sw91-communication/types/com.base';
import { Colors } from '@tls/treact-ui';
import {
  ChartData,
  CoreChartOptions,
  DatasetChartOptions,
  ElementChartOptions,
  LineControllerChartOptions,
  PluginChartOptions,
  ScaleChartOptions,
} from 'chart.js';
import { _DeepPartialObject } from 'chart.js/dist/types/utils';
import { AnnotationOptions } from 'chartjs-plugin-annotation';
import { getColorOption } from 'components/common/ColorOptions';

export type Coordinate = {
  x: number;
  y: number;
};

export type ModelBoxPlotChartType = ChartData<'scatter', Coordinate[], string>;
export function getChartObject() {
  return {
    datasets: [],
  } as ModelBoxPlotChartType;
}

export function getDefaultDataSet(modelClass: ModelClassMessage) {
  return {
    label: modelClass?.className,
    data: [],
    pointBorderWidth: [],
    pointRadius: [],
    pointBorderColor: [],
    hoverBorderColor: `rgb(${getColorOption(modelClass?.labelColor).id})`,
    borderColor: `rgb(${getColorOption(modelClass?.labelColor).id})`,
    pointStyle: [],
    pointHoverRadius: 8,
    pointHoverBorderWidth: 2,
  };
}

export type OptionType = _DeepPartialObject<
  CoreChartOptions<'scatter'> &
    ElementChartOptions<'scatter'> &
    PluginChartOptions<'scatter'> &
    DatasetChartOptions<'scatter'> &
    ScaleChartOptions<'scatter'> &
    AnnotationOptions<'box'> &
    LineControllerChartOptions
>;

export function getDefaultOption(amountModelClasses: number): OptionType {
  return {
    responsive: true,
    animation: false,
    clip: false,
    font: {
      family: 'Open Sans, sans-serif',
    },
    plugins: {
      legend: {
        display: false,
      },
      annotation: {
        annotations: {},
        animations: {
          duration: 100,
        },
      },
      tooltip: {
        backgroundColor: Colors.trwhite.hex,
        titleColor: Colors.trblack.hex,
        bodyColor: Colors.trblack.hex,
        borderColor: Colors.trblack.hex,
        footerColor: Colors.trblack.hex,
        borderWidth: 0.3,
        boxPadding: 5,
        cornerRadius: 0,
        callbacks: {
          label: () => '',
        },
      },
      zoom: {
        zoom: {
          wheel: {
            enabled: true,
          },
          pinch: {
            enabled: true,
          },
          mode: 'y',
        },
        limits: {
          y: {
            max: 1,
            min: 0,
          },
        },
        pan: {
          enabled: true,
          mode: 'y',
        },
      },
    },
    backgroundColor: Colors.trgrey7.hex,
    borderWidth: 1,
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          display: false,
          precision: 0,
        },
        border: {
          display: false,
        },
        max: (amountModelClasses * 10) / 2 - 2.5,
        min: -2.5,
      },
      y: {
        grid: {
          display: false,
        },
        ticks: {
          display: true,
          padding: 5,
        },
        border: {
          display: true,
          color: Colors.trgrey4.hex,
        },
        max: 1,
        min: -0,
        title: {
          text: 'IoU',
          display: true,
          font: {
            weight: 'bold',
          },
          color: Colors.trblack.hex,
        },
      },
    },
    layout: {
      padding: {
        right: 0,
        left: 4,
        top: 20,
        bottom: 10,
      },
    },

    maintainAspectRatio: false,
  };
}
